import Reveal from "@/components/Reveal";
import { Snowflake, ThermometerSnowflake, Pill, UtensilsCrossed, IceCreamCone, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Snowflake,
    span: "md:col-span-7",
    title: "Refrigerated same-day courier",
    body: "Point-to-point chilled delivery across Greater Sydney in dedicated 0-4°C vehicles. Booked by 11am, delivered by close of business.",
    tag: "0-4°C",
  },
  {
    icon: ThermometerSnowflake,
    span: "md:col-span-5",
    title: "Frozen transport",
    body: "Deep-frozen freight held at -18°C and below, from single cartons to full pallets, with temperature printouts on delivery.",
    tag: "-18°C",
  },
  {
    icon: Pill,
    span: "md:col-span-5",
    title: "Pharmaceutical cold chain",
    body: "GDP-conscious 2-8°C transport for vaccines, pathology and clinical supplies. Validated vehicles, trained drivers, full audit trail.",
    tag: "2-8°C",
  },
  {
    icon: UtensilsCrossed,
    span: "md:col-span-7",
    title: "Hospitality, events & produce",
    body: "Early-morning market runs, multi-drop restaurant routes and event catering logistics across the metro — timed to your service window.",
    tag: "Multi-drop",
  },
  {
    icon: IceCreamCone,
    span: "md:col-span-12",
    title: "Deep-frozen & ice cream transport",
    body: "Purpose-built -25°C holds for ice cream, gelato and frozen desserts — moved from factory floor to retail freezer without a single melt cycle, with temperature printouts on every delivery.",
    tag: "-25°C",
  },
];

const Services = () => (
  <section id="services" data-testid="services-section" className="relative py-32 lg:py-40 bg-surface/30 border-y border-white/10">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
      <Reveal className="flex flex-wrap items-end justify-between gap-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost font-bold mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-frost inline-block" />
            What we move
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase font-medium">
            Cold services,<br />sharp execution
          </h2>
        </div>
        <p className="max-w-md text-mist font-light leading-relaxed">
          Four specialised services, one promise: the setpoint you specify is the
          temperature your freight travels at — start to finish.
        </p>
      </Reveal>

      <div className="mt-20 grid md:grid-cols-12 gap-6">
        {SERVICES.map((svc, i) => (
          <Reveal key={svc.title} delay={i * 0.1} className={svc.span}>
            <article
              data-testid={`service-card-${i}`}
              className="group relative h-full border border-white/10 bg-abyss p-10 lg:p-12 overflow-hidden hover:border-frost/40 transition-colors duration-500"
            >
              <div className="absolute -top-16 -right-16 w-48 h-48 bg-frost/5 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" aria-hidden="true" />
              <div className="flex items-start justify-between">
                <svc.icon className="w-8 h-8 text-ice group-hover:text-frost transition-colors duration-500" strokeWidth={1.25} />
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-frost border border-frost/30 px-3 py-1">
                  {svc.tag}
                </span>
              </div>
              <h3 className="font-display text-2xl lg:text-3xl font-medium tracking-tight mt-10 max-w-sm">
                {svc.title}
              </h3>
              <p className="mt-5 text-mist font-light leading-relaxed max-w-md">{svc.body}</p>
              <a
                href="#quote"
                data-testid={`service-quote-link-${i}`}
                className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-ice group-hover:text-frost transition-colors duration-500"
              >
                Request this service
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" strokeWidth={1.5} />
              </a>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Services;
